// src/screens/SetPasswordScreen.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import api from '../services/api';

const SetPasswordScreen = () => {
  // --- Hooks ---
  const { token } = useParams(); // Gets the ':token' from the URL
  const navigate = useNavigate();

  // --- State Management ---
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [message, setMessage] = useState(''); // For success messages
  const [error, setError] = useState('');     // For error messages

  const [loading, setLoading] = useState(false); // For form submission spinner
  const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking', 'valid', 'invalid'

  // --- useEffect to verify the token on component mount ---
  useEffect(() => {
    const verifyToken = async () => {
      try {
        // This GET request hits your backend route: /api/users/verify-token/:token
        await api.get(`/user/verify-token/${token}`);
        setTokenStatus('valid');
      } catch (err) {
        setError(err.response?.data?.message || 'An unexpected error occurred.');
        setTokenStatus('invalid');
      }
    };

    verifyToken();
  }, [token]); // This effect runs whenever the token in the URL changes

  // --- Form Submission Handler ---
  const submitHandler = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) { // A basic security check
        setError('Password must be at least 6 characters long.');
        return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      // This PUT request hits your backend route: /api/users/set-password
      const { data } = await api.put('/user/set-password', {
        token,
        password,
      });

      setLoading(false);
      setMessage(data.message);

      // Redirect to login page after a short delay to show the success message
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Failed to set password.');
    }
  };

  // --- Conditional Rendering Logic ---
  const renderContent = () => {
    switch (tokenStatus) {
      case 'checking':
        return (
          <div className="text-center">
            <Spinner animation="border" role="status" />
            <p className="mt-2">Verifying your link...</p>
          </div>
        );
      
      case 'invalid':
        return (
          <Alert variant="danger">
            <Alert.Heading>Link Expired or Invalid</Alert.Heading>
            <p>{error}</p>
            <p>Please contact your manager to be sent a new setup link.</p>
          </Alert>
        );

      case 'valid':
        return (
          <Card>
            <Card.Body>
              <Card.Title as="h3" className="text-center mb-4">Set Your Password</Card.Title>
              
              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={submitHandler}>
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading || message} // Disable after success
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="confirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading || message}
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button variant="primary" type="submit" disabled={loading || message}>
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                        <span className="ms-2">Setting Password...</span>
                      </>
                    ) : (
                      'Set Password & Activate Account'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
      <div style={{ width: '100%', maxWidth: '450px' }}>
        {renderContent()}
      </div>
    </Container>
  );
};

export default SetPasswordScreen;